import type { BoardDef, Roster } from '@archon3d/core';
import { useEffect, useState } from 'react';
import type { RadioPlayer } from '../../audio/radio';
import { GameEditor } from '../../editor/GameEditor';
import type { EditorStore } from '../../editor/editorStore';
import type { ServerStatus } from '../../net/serverClient';
import type { RoomController } from '../../net/useRoom';
import type { GameSetup } from '../gameSetup';
import type { Settings } from '../settings';
import { BoardPicker } from './BoardPicker';
import { ConfirmDialog } from './ConfirmDialog';
import { GameSetupPanel } from './GameSetupPanel';
import { InGameSettings } from './InGameSettings';
import { HostPanel, JoinPanel } from './OnlinePanels';
import { ReplaysPanel } from './ReplaysPanel';
import { SettingsPanel } from './SettingsPanel';
import { serverLabel } from './serverLabel';
import { AccountDialog, LeaderboardPanel, RecommendPanel } from './AccountPanel';
import { accountRequest, requestError, saveSession, type Account } from '../../net/accounts';
import {
  clearRemoteGameLibrary,
  gameDefinition,
  refreshGameLibrary,
} from '../../editor/gameLibrary';
import { CloudLibraryPanel } from './CloudLibraryPanel';
import { RuleBookPanel } from './RuleBookPanel';

export type MenuPanel =
  | 'home'
  | 'new'
  | 'boards'
  | 'editor'
  | 'host'
  | 'join'
  | 'replays'
  | 'settings'
  | 'account'
  | 'leaderboard'
  | 'cloud'
  | 'recommend'
  | 'rules';

export interface HomeScreenProps {
  /** Why the game kept in this browser could not be continued, when it could not. */
  lostSave?: string | null;
  panel: MenuPanel;
  onPanel: (panel: MenuPanel) => void;
  /** Opens the setup panel on the standing defaults; see the app's `openSetup`. */
  onNewGame: () => void;
  /** Starts a game against the AI on the defaults at once, with no panel in between. */
  onQuickGame: () => void;
  /** Boards that ship with the game. */
  boards: BoardDef[];
  /** Characters on the current table, including summoned elements. */
  roster: Roster;
  /** Boards the retired board editor saved in this browser, offered for import into a game. */
  savedBoards: BoardDef[];
  cloudGame: () => { id: string; name: string; data: unknown };
  onCloudGame: (data: unknown) => void;
  /** Why a game cannot be saved to, or loaded from, the account just now; `null` when it can. */
  cloudSaveBlocked?: string | null;
  cloudLoadBlocked?: string | null;
  /** The editor's draft, kept by the app so it survives leaving the panel. */
  editor: EditorStore;
  setup: GameSetup;
  onSetup: (setup: GameSetup) => void;
  /** Starts a new game with the current setup. */
  onStart: () => void;
  /** A game is on the table behind the menu: one in progress, or the last one, brought back. */
  canResume: boolean;
  /** The game on the table is over: the same game again, offered first (none online). */
  onRematch?: () => void;
  /** What that game is, for the Continue button: "Light vs AI · turn 12". */
  resumeLabel: string;
  onResume: () => void;
  /** Throws the game on the table away and comes back to the title. */
  onQuitGame: () => void;
  onResetView?: () => void;
  radio: RadioPlayer;
  settings: Settings;
  onSettings: (patch: Partial<Settings>) => void;
  onResetSettings: () => void;
  /** Take the guided first game's lessons again, from the first. */
  onResetGuide: () => void;
  server: ServerStatus;
  /** Hosting and joining games on the server. */
  room: RoomController;
  xrSupported: boolean | null;
  onEnterVr: () => void;
  /** The tab was in the headset when its page went (a reload, a deploy): offer the way back. */
  returnToVr?: boolean;
  /** Back into the headset, and into the kept game if there is one. */
  onReturnToVr?: () => void;
  version: string;
}

/**
 * The home screen over the live table: where a game is continued, set up, hosted or joined and
 * where the preferences live. Plain DOM, so it is the desktop and the 2D headset browser's front
 * door; inside VR the tablets on the table carry the same choices.
 */
export function HomeScreen(props: HomeScreenProps) {
  const { panel, onPanel } = props;
  /**
   * The rule book goes back where it was opened from: the menu when read from the menu, the game
   * when read from the table's Rules button (client-tablets-ui:bugs#10).
   */
  const [rulesFromMenu, setRulesFromMenu] = useState(false);
  const [accountMode, setAccountMode] = useState<'login' | 'register'>('login');
  const [user, setUser] = useState<Account | null>(null);
  const [accountError, setAccountError] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  useEffect(() => {
    let alive = true;
    const refresh = () => {
      void accountRequest<{ user: Account | null }>('account')
        .then((r) => {
          if (alive) setUser(r.user);
        })
        .catch(() => {
          if (alive) setUser(null);
        });
    };
    refresh();
    window.addEventListener('arena-account-changed', refresh);
    return () => {
      alive = false;
      window.removeEventListener('arena-account-changed', refresh);
    };
  }, []);
  const all = [...props.boards, ...props.savedBoards];
  // A game from the library brings its own board, as the app resolves it; the bundled ones used
  // to stand in for it here, so the board picker and the rule book spoke of another game
  // (client-tablets-ui:quality#18).
  const board =
    gameDefinition(props.setup.boardId)?.board ??
    all.find((b) => b.id === props.setup.boardId) ??
    props.boards[0]!;
  // With a game on the table the settings are the only panel left, so they open over the menu
  // rather than replacing it: the way back to the game stays in sight behind them.
  const settingsOverGame = props.canResume && panel === 'settings';
  const closeSettings = () => onPanel('home');
  return (
    <div className="menu" data-testid="menu" role="dialog" aria-label="RPG-Arena menu">
      <div
        className={
          panel === 'editor'
            ? 'menu-card editor-card'
            : panel === 'rules'
              ? 'menu-card rule-card'
              : 'menu-card'
        }
      >
        {(panel === 'home' || panel === 'account') && (
          <div className="home-account-links">
            {user ? (
              <>
                <a
                  href="#profile"
                  onClick={(e) => {
                    e.preventDefault();
                    setAccountMode('login');
                    onPanel('account');
                  }}
                >
                  {user.name}
                </a>
                <a
                  href="#logout"
                  aria-disabled={loggingOut}
                  onClick={(e) => {
                    e.preventDefault();
                    if (loggingOut) return;
                    setLoggingOut(true);
                    setAccountError('');
                    void accountRequest('logout', {})
                      .then(() => {
                        saveSession('');
                        setUser(null);
                        clearRemoteGameLibrary();
                        void refreshGameLibrary().catch(() => {});
                      })
                      .catch((error) => setAccountError(requestError(error)))
                      .finally(() => setLoggingOut(false));
                  }}
                >
                  Logout
                </a>
              </>
            ) : (
              <>
                <a
                  href="#login"
                  onClick={(e) => {
                    e.preventDefault();
                    setAccountMode('login');
                    onPanel('account');
                  }}
                >
                  Login
                </a>
                <a
                  href="#register"
                  onClick={(e) => {
                    e.preventDefault();
                    setAccountMode('register');
                    onPanel('account');
                  }}
                >
                  Register
                </a>
              </>
            )}
            {accountError && <span role="alert">{accountError}</span>}
          </div>
        )}
        {panel === 'account' && (
          <AccountDialog initialMode={accountMode} onBack={() => onPanel('home')} />
        )}
        {panel === 'leaderboard' && <LeaderboardPanel onBack={() => onPanel('home')} />}
        {panel === 'recommend' && <RecommendPanel onBack={() => onPanel('home')} />}
        {panel === 'cloud' && (
          <>
            <CloudLibraryPanel
              kind="save"
              current={props.cloudGame}
              onLoad={props.onCloudGame}
              saveBlocked={props.cloudSaveBlocked ?? null}
              loadBlocked={props.cloudLoadBlocked ?? null}
            />
            <button onClick={() => onPanel('home')}>Back</button>
          </>
        )}
        {panel === 'rules' && (
          <RuleBookPanel
            roster={props.roster}
            board={board}
            onBack={() => {
              const toMenu = rulesFromMenu || !props.canResume;
              setRulesFromMenu(false);
              if (toMenu) onPanel('home');
              else props.onResume();
            }}
          />
        )}
        {(panel === 'home' || panel === 'account' || settingsOverGame) && (
          <Home
            {...props}
            board={board}
            onRules={() => {
              setRulesFromMenu(true);
              onPanel('rules');
            }}
          />
        )}
        {panel === 'new' && (
          <GameSetupPanel
            mode="local"
            boards={props.boards}
            savedBoards={props.savedBoards}
            setup={props.setup}
            onSetup={props.onSetup}
            onBoards={() => onPanel('boards')}
            onBack={() => onPanel('home')}
            onStart={props.onStart}
            startLabel="Start"
          />
        )}
        {panel === 'boards' && (
          <BoardPicker
            bundled={props.boards}
            saved={props.savedBoards}
            chosen={board.id}
            onChoose={(id) => {
              props.onSetup({ ...props.setup, boardId: id });
              onPanel('new');
            }}
            onEdit={() => onPanel('editor')}
            onBack={() => onPanel('new')}
          />
        )}
        {panel === 'editor' && (
          <GameEditor
            savedBoards={props.savedBoards}
            store={props.editor}
            signedIn={user !== null}
            onPlay={(id) => {
              props.onSetup({ ...props.setup, boardId: id });
              onPanel('new');
            }}
            onBack={() => onPanel('home')}
          />
        )}
        {panel === 'host' && (
          <HostPanel
            server={props.server}
            boards={props.boards}
            setup={props.setup}
            room={props.room}
            onBack={() => onPanel('home')}
          />
        )}
        {panel === 'join' && (
          <JoinPanel
            server={props.server}
            boards={props.boards}
            room={props.room}
            onBack={() => onPanel('home')}
          />
        )}
        {panel === 'replays' && (
          <ReplaysPanel
            server={props.server}
            boards={props.boards}
            room={props.room}
            onBack={() => onPanel('home')}
          />
        )}
        {panel === 'settings' && !settingsOverGame && (
          <SettingsPanel
            settings={props.settings}
            onChange={props.onSettings}
            onReset={props.onResetSettings}
            onResetGuide={props.onResetGuide}
            onBack={closeSettings}
            radio={props.radio}
          />
        )}
        {panel !== 'home' && panel !== 'account' && panel !== 'editor' && panel !== 'rules' && (
          <footer className="menu-footer">
            <>
              <span className="pill" data-testid="server-status">
                <span className={`dot ${serverDot(props.server)}`} />
                {serverLabel(props.server)}
              </span>
              <span className="version">v{props.version}</span>
            </>
          </footer>
        )}
      </div>
      {settingsOverGame && (
        <InGameSettings onClose={closeSettings}>
          <SettingsPanel
            inGame
            settings={props.settings}
            onChange={props.onSettings}
            onReset={props.onResetSettings}
            onResetGuide={props.onResetGuide}
            onBack={closeSettings}
            radio={props.radio}
          />
        </InGameSettings>
      )}
    </div>
  );
}

function Home(props: HomeScreenProps & { board: BoardDef; onRules: () => void }) {
  const [confirmQuit, setConfirmQuit] = useState(false);
  // New game, Host, Join, the editor and the replays all replace whatever is on the table. Rather
  // than put a confirmation on each of them, the menu stops offering them while a game is running:
  // Quit is the one door out, and it names what is lost. Settings and VR leave the game alone, so
  // they stay. Quitting brings the rest back.
  const playing = props.canResume;
  return (
    <>
      <h1 className="title">RPG-Arena</h1>
      {/* Under the title, where a tagline once said what the game is (the owner, 2026-09-26). */}
      <p className="subtitle recommend">
        <a
          href="#recommend"
          data-testid="menu-recommend"
          onClick={(e) => {
            e.preventDefault();
            props.onPanel('recommend');
          }}
        >
          Recommend to a friend
        </a>
      </p>
      {props.lostSave && (
        <p className="subtitle" role="status" data-testid="menu-lost-save">
          {props.lostSave}
        </p>
      )}
      <div className="menu-buttons">
        {props.returnToVr && props.xrSupported === true && props.onReturnToVr && (
          <button className="primary" onClick={props.onReturnToVr} data-testid="menu-return-vr">
            Return to VR
          </button>
        )}
        {playing ? (
          <button className="primary" onClick={props.onResume} data-testid="menu-resume">
            Continue game
          </button>
        ) : (
          <>
            {props.onRematch && (
              <button className="primary" onClick={props.onRematch} data-testid="menu-rematch">
                Rematch
              </button>
            )}
            {/* One tap to a first game; the choices are behind Custom game for later. */}
            <button
              className={props.onRematch ? undefined : 'primary'}
              onClick={props.onQuickGame}
              data-testid="menu-quick"
            >
              Play vs AI
            </button>
            <button onClick={props.onNewGame} data-testid="menu-play">
              Custom game…
            </button>
          </>
        )}
        <button onClick={props.onRules} data-testid="menu-rules">
          Rule Book
        </button>
      </div>
      {/* Everything else, lighter: none of it is the way into a first game. */}
      <div className="menu-buttons secondary">
        {!playing && (
          <>
            <button onClick={() => props.onPanel('host')} data-testid="menu-host">
              Host game
            </button>
            <button onClick={() => props.onPanel('join')} data-testid="menu-join">
              Join game
            </button>
            <button onClick={() => props.onPanel('editor')} data-testid="menu-editor">
              Editor
            </button>
          </>
        )}
        <button onClick={() => props.onPanel('cloud')}>Game Storage</button>
        <button onClick={() => props.onPanel('settings')} data-testid="menu-settings">
          Settings
        </button>
        {playing && <button onClick={props.onResetView}>Reset View</button>}
        {playing && (
          <button className="danger" onClick={() => setConfirmQuit(true)} data-testid="menu-quit">
            Quit game
          </button>
        )}
      </div>
      <LeaderboardPanel server={props.server} />
      {confirmQuit && (
        <ConfirmDialog
          title="Quit game?"
          detail={`${props.resumeLabel} will be lost.`}
          confirmLabel="Quit"
          onConfirm={() => {
            setConfirmQuit(false);
            props.onQuitGame();
          }}
          onCancel={() => setConfirmQuit(false)}
        />
      )}
    </>
  );
}

function serverDot(server: ServerStatus): string {
  switch (server.state) {
    case 'online':
      return 'ok';
    case 'offline':
      return 'bad';
    case 'unconfigured':
      return '';
    default:
      return 'warn';
  }
}
